// A rep's own numbers for "My numbers" (WEB_PLAN.md §5.3, §5.10): `team_metrics(p_company, p_from, p_to)` through
// React Query. A rep's replica is filtered (revocations, other people's history), so counting it would undercount;
// the server counts over everything, exactly as `metricValue` does for admins.
//
// The RPC is outside sync, so a pull does not refresh it. Instead the numbers follow the replica: when the synced
// activities change (a visit logged on the iPhone arrived), they refetch shortly after, once for a burst of changes.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useSession } from '@/auth/AuthProvider';
import { callRpc } from '@/auth/rpc';
import type { GoalPeriod } from '@/contracts/enums';
import type { MetricsRow } from '@/contracts/wire';
import { toWireDate } from '@/contracts/wireDate';
import { useActivities } from '@/data/hooks';
import { REFRESH_EVENT } from '@/features/shell/refresh';
import { decodeMetricsRows } from './dashboardModel';

export const TEAM_METRICS_QUERY_KEY = ['team_metrics'] as const;

/**
 * How long after the synced activities change the numbers refetch. Changes inside the window ride along (a first
 * sync or a busy pull changes the list many times), so a burst costs one RPC.
 */
export const ACTIVITY_REFETCH_DELAY_MS = 10_000;

/**
 * The `team_metrics` rows from the period start to now. The key holds the period start (not "now"), so the numbers
 * refetch when the day, week, month or quarter rolls over, about 10 seconds after the synced activities change, every
 * two minutes while the tab is visible, on focus, and when the shell's Refresh button is pressed; not on every render.
 */
export function useTeamMetricsQuery(period: GoalPeriod, from: Date, enabled: boolean) {
  const session = useSession();
  const queryClient = useQueryClient();
  const fromWire = toWireDate(from);

  useEffect(() => {
    const refresh = () => void queryClient.invalidateQueries({ queryKey: TEAM_METRICS_QUERY_KEY });
    window.addEventListener(REFRESH_EVENT, refresh);
    return () => window.removeEventListener(REFRESH_EVENT, refresh);
  }, [queryClient]);

  // The activities list keeps its identity until the collection changes (data/hooks), so a new list means a pull
  // brought or removed activities. The list on mount is what the first fetch already counts.
  const activities = useActivities();
  const seen = useRef(activities);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (activities === seen.current) return;
    seen.current = activities;
    if (!enabled || timer.current !== null) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      void queryClient.invalidateQueries({ queryKey: TEAM_METRICS_QUERY_KEY });
    }, ACTIVITY_REFETCH_DELAY_MS);
  }, [activities, enabled, queryClient]);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    },
    [],
  );

  return useQuery({
    queryKey: [...TEAM_METRICS_QUERY_KEY, session.companyId, session.profileId.toLowerCase(), period, fromWire],
    queryFn: (): Promise<MetricsRow[]> =>
      callRpc<unknown>('team_metrics', {
        p_company: session.companyId,
        p_from: fromWire,
        p_to: toWireDate(new Date()),
      }).then(decodeMetricsRows),
    enabled,
    // While the tab is visible (React Query pauses intervals in the background).
    refetchInterval: 120_000,
  });
}
